import { useEffect, useRef, useState } from "react";
import { api, API, getUser, setUser } from "../lib/api";
import { openExternal } from "../lib/native";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ShieldCheck, FileText, Truck, Building2, Upload, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// Task 3 — Carrier readiness card. Wraps three fields (Public Liability
// insurance, disposal facility, vehicle types) that a carrier must fill
// in before they can bid on jobs. Rendered on the Dashboard.
const VEHICLES = [
  { key: "car", label: "Car" },
  { key: "small_van", label: "Small van" },
  { key: "large_van", label: "Large van" },
  { key: "luton", label: "Luton" },
  { key: "flatbed", label: "Flatbed" },
  { key: "hgv", label: "HGV" },
];

const LABELS = {
  insurance_document: "Public Liability insurance",
  disposal_facility: "Disposal facility details",
  vehicle_types: "Vehicle types",
};

export default function CarrierComplianceCard({ onChanged }) {
  const user = getUser();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [facilityName, setFacilityName] = useState(user?.disposal_facility_name || "");
  const [facilityPermit, setFacilityPermit] = useState(user?.disposal_facility_permit || "");
  const [vehicles, setVehicles] = useState(user?.vehicle_types || []);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => {
    if (!user || user.role !== "carrier") return;
    (async () => {
      try {
        const { data } = await api.get(`/carriers/${user.id}/readiness`, { params: { user_id: user.id } });
        setReadiness(data);
      } catch {
        setReadiness(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user || user.role !== "carrier") return null;

  const refreshUser = async () => {
    const { data: fresh } = await api.get(`/auth/me/${user.id}`);
    setUser(fresh);
    const { data: rd } = await api.get(`/carriers/${user.id}/readiness`, { params: { user_id: user.id } });
    setReadiness(rd);
    onChanged?.();
  };

  const uploadInsurance = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("user_id", user.id);
      await api.post(`/carriers/insurance`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Insurance uploaded");
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const toggleVehicle = (key) => {
    setVehicles((v) => (v.includes(key) ? v.filter((x) => x !== key) : [...v, key]));
  };

  const saveOnboarding = async () => {
    setSaving(true);
    try {
      await api.patch(
        `/carriers/${user.id}/onboarding`,
        {
          disposal_facility_name: facilityName.trim(),
          disposal_facility_permit: facilityPermit.trim(),
          vehicle_types: vehicles,
        },
        { params: { user_id: user.id } }
      );
      toast.success("Profile updated");
      await refreshUser();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const missing = readiness?.missing || [];
  const missingNonLicence = missing.filter((m) => m !== "license_verified");
  const ready = readiness?.ready;

  return (
    <Card
      data-testid="carrier-compliance-card"
      className={`md:col-span-2 p-5 rounded-2xl border ${
        ready ? "border-[#4A6B56]/30 bg-[#4A6B56]/5" : missingNonLicence.length > 0 ? "border-[#D67B54] bg-[#FDF2EB]" : "border-[#E2DFD8] bg-[#F6F4F0]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            ready ? "bg-[#4A6B56] text-white" : "bg-[#D67B54] text-white"
          }`}
        >
          {ready ? <CheckCircle2 className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
        </div>
        <div className="flex-1">
          <div className="font-medium" style={{ fontFamily: "Outfit" }}>
            {ready ? "You're ready to bid" : "Finish carrier onboarding to start bidding"}
          </div>
          {!ready && missingNonLicence.length > 0 && (
            <p data-testid="compliance-missing" className="text-sm text-[#1B3624] mt-1">
              Missing:{" "}
              <span className="font-medium">
                {missingNonLicence.map((m) => LABELS[m] || m).join(", ")}
              </span>
              . Finish the fields below to unlock bidding.
            </p>
          )}
          {!ready && missing.includes("license_verified") && missingNonLicence.length === 0 && (
            <p className="text-sm text-[#6B6965] mt-1">
              These onboarding fields are complete. Your waste-carrier licence is still awaiting verification.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 grid md:grid-cols-2 gap-5">
        {/* Insurance */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[#1B3624]">
            <ShieldCheck className="w-4 h-4" />
            <div className="text-sm font-medium">Public Liability insurance</div>
          </div>
          <p className="text-xs text-[#6B6965]">Must cover waste removal. PDF or image, max 8MB.</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => uploadInsurance(e.target.files?.[0])}
            className="hidden"
            data-testid="insurance-file-input"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              data-testid="insurance-upload-btn"
              variant="outline"
              disabled={uploading}
              onClick={() => fileRef.current?.click()}
              className="rounded-full border-[#1B3624] text-[#1B3624] min-h-[44px]"
            >
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
              {user.insurance_document_url ? "Replace document" : "Upload insurance"}
            </Button>
            {user.insurance_document_url && (
              <a
                href={`${API.replace(/\/api$/, "")}${user.insurance_document_url}`}
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(`${API.replace(/\/api$/, "")}${user.insurance_document_url}`);
                }}
                data-testid="insurance-current-link"
                className="inline-flex items-center gap-1 text-xs text-[#4A6B56] hover:underline"
              >
                <FileText className="w-3 h-3" /> View current
              </a>
            )}
          </div>
        </div>

        {/* Disposal facility */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-[#1B3624]">
            <Building2 className="w-4 h-4" />
            <div className="text-sm font-medium">Disposal facility</div>
          </div>
          <p className="text-xs text-[#6B6965]">Where your waste is legally disposed. Included on duty-of-care receipts.</p>
          <div className="grid gap-2">
            <div>
              <Label className="text-xs">Facility name</Label>
              <Input
                data-testid="facility-name-input"
                value={facilityName}
                onChange={(e) => setFacilityName(e.target.value)}
                placeholder="e.g. Biffa MRF Manchester"
                className="rounded-xl bg-white mt-1"
                maxLength={120}
              />
            </div>
            <div>
              <Label className="text-xs">Site permit number (EA/NRW)</Label>
              <Input
                data-testid="facility-permit-input"
                value={facilityPermit}
                onChange={(e) => setFacilityPermit(e.target.value)}
                placeholder="e.g. EPR/BB1234AB"
                className="rounded-xl bg-white mt-1"
                maxLength={60}
              />
            </div>
          </div>
        </div>

        {/* Vehicle types */}
        <div className="md:col-span-2 space-y-2">
          <div className="flex items-center gap-2 text-[#1B3624]">
            <Truck className="w-4 h-4" />
            <div className="text-sm font-medium">Vehicle types</div>
          </div>
          <p className="text-xs text-[#6B6965]">Select every vehicle you can dispatch. Used to match jobs to your fleet.</p>
          <div className="flex flex-wrap gap-2">
            {VEHICLES.map((v) => {
              const active = vehicles.includes(v.key);
              return (
                <button
                  key={v.key}
                  type="button"
                  data-testid={`vehicle-pill-${v.key}`}
                  onClick={() => toggleVehicle(v.key)}
                  className={`rounded-full px-4 min-h-[36px] text-sm border transition-colors ${
                    active
                      ? "bg-[#1B3624] text-white border-[#1B3624]"
                      : "bg-white text-[#1B3624] border-[#E2DFD8] hover:border-[#1B3624]"
                  }`}
                >
                  {v.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end">
        <Button
          data-testid="compliance-save-btn"
          onClick={saveOnboarding}
          disabled={saving}
          className="rounded-full bg-[#1B3624] hover:bg-[#274d33] text-white px-6 min-h-[44px]"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save profile"}
        </Button>
      </div>
    </Card>
  );
}
